import { i18n, type Messages } from "@lingui/core";
import { detect, fromStorage, fromNavigator } from "@lingui/detect-locale";

export const supportedLocales = ["en", "lt", "ru"] as const;
export type SupportedLocale = (typeof supportedLocales)[number];
export const defaultLocale: SupportedLocale = "en";

const LOCALE_STORAGE_KEY = "locale";

export const supportedBrands = ["cultz", "reactor"] as const;
export type Brand = (typeof supportedBrands)[number];

const envBrand = import.meta.env.VITE_BRAND as Brand | undefined;
export const currentBrand: Brand = (
  supportedBrands as readonly string[]
).includes(envBrand as Brand)
  ? (envBrand as Brand)
  : "cultz";

type CatalogModule = { messages: Messages };
const baseCatalogImports = import.meta.glob<CatalogModule>(
  "../locales/*/messages.po",
);
const brandCatalogImports = import.meta.glob<CatalogModule>(
  "../locales/*/brands/*.po",
);

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const isSupported = (code: string): code is SupportedLocale =>
  (supportedLocales as readonly string[]).includes(code);

function normalizeToSupportedLocale(
  input: string | null | undefined,
): SupportedLocale {
  const raw = (input ?? defaultLocale).toLowerCase().replace(/_/g, "-");
  if (isSupported(raw)) return raw;
  const base = raw.split("-")[0];
  return isSupported(base) ? (base as SupportedLocale) : defaultLocale;
}

export function detectInitialLocale(): SupportedLocale {
  if (!isBrowser()) return defaultLocale;
  const detected = detect(
    fromStorage(LOCALE_STORAGE_KEY),
    fromNavigator(),
    defaultLocale,
  );
  return normalizeToSupportedLocale(detected);
}

async function loadBaseMessages(locale: SupportedLocale): Promise<Messages> {
  const key = `../locales/${locale}/messages.po`;
  const importer = baseCatalogImports[key];
  if (!importer) {
    throw new Error(`Missing catalog for ${locale} at ${key}`);
  }
  const mod = await importer();
  return mod.messages;
}

async function loadBrandMessages(
  locale: SupportedLocale,
  brand: Brand,
): Promise<Messages> {
  const key = `../locales/${locale}/brands/${brand}.po`;
  const importer = brandCatalogImports[key];
  if (!importer) {
    return {};
  }
  const mod = await importer();
  return mod.messages;
}

export async function activateLocale(
  locale: SupportedLocale = defaultLocale,
  brand: Brand = currentBrand,
): Promise<void> {
  const [baseMessages, brandMessages] = await Promise.all([
    loadBaseMessages(locale),
    loadBrandMessages(locale, brand),
  ]);
  i18n.loadAndActivate({
    locale,
    messages: { ...baseMessages, ...brandMessages },
  });
  if (isBrowser()) {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    document.documentElement.setAttribute("lang", locale);
  }
}

export async function initI18n(): Promise<SupportedLocale> {
  const initial = detectInitialLocale();
  await activateLocale(initial, currentBrand);
  return initial;
}

/**
 * Helper to translate brand-specific keys without triggering main Lingui extraction.
 * Use this for brand keys that should only be extracted from brand-keys.ts.
 * @param id - The translation key (e.g., from BrandMessageIds)
 * @returns The translated string
 */
export function translateBrand(id: string): string {
  return i18n._(id);
}

export { i18n };
